
import { Modal } from 'bootstrap';

import { defineComponent, ref, inject, watchEffect, onUnmounted } from 'vue';

import { Apollo } from '@/core/services';

import { GET_GROUP_PRODUCTS } from '@/modules/catalog/product-groups/graphql/Queries';

import { useI18n } from 'vue-i18n';

import InnerLoader from '@/components/InnerLoader.vue';

import Table from '@/components/Table/Table.vue';

const PAGE_LIMIT = 10;

export default defineComponent({
    name: 'view customer group',

    components: {
        Table,

        InnerLoader
    },

    setup() {
        const loader = ref(false);

        const products = ref<any[]>([]);

        const modal: any = ref();

        const loading = ref(false);

        const emitter: any = inject('emitter');

        const current_page = ref(1);

        const i18n = useI18n();

        const product_group_id = ref<number | undefined>();

        const systemLocale = ref<string>();

        const pagination = ref({}) as Record<string, any>;

        const clickHandler = ref() as Record<any, any>;

        const columns = ref([
            {
                label: 'message.NAME',

                key: 'name',

                sorting: true
            },

            {
                label: 'message.PRODUCT_NUMBER',

                key: 'economic_number',

                sorting: true
            },

            {
                label: 'message.IS_SHOP_PRODUCT',

                key: 'is_shop_product',

                sorting: false
            },

            {
                label: 'message.QUANTITY',

                key: 'available_stock',

                sorting: true
            },

            {
                label: 'message.STATUS',

                key: 'status'
            }
        ]);

        watchEffect(() => {
            systemLocale.value = String(i18n.fallbackLocale.value ?? 'en');
        });

        const localeShort = () => {
            const loc = String(systemLocale.value ?? 'en');

            return loc.split('-')[0] || 'en';
        };

        const productDisplayName = (item: Record<string, unknown>) => {
            try {
                const desc = item?.description as { name?: string } | undefined;

                const raw = desc?.name;

                if (!raw || typeof raw !== 'string') return '';

                const parsed = JSON.parse(raw) as Record<string, { name?: string } | undefined>;

                const loc = localeShort();

                return parsed[loc]?.name ?? parsed.en?.name ?? '';
            } catch {
                return '';
            }
        };

        const getGroupProducts = async (page = 1, reload = false) => {
            loader.value = true;

            try {
                reload === true ? (current_page.value = 1) : false;

                clickHandler.value = getGroupProducts;

                const result = await Apollo.query({
                    query: GET_GROUP_PRODUCTS,

                    errorPolicy: 'all',

                    fetchPolicy: 'network-only',

                    variables: {
                        page,

                        limit: PAGE_LIMIT,

                        product_group_id: product_group_id.value
                    }
                });

                if (result.errors?.length) {
                    products.value = [];

                    pagination.value = {};

                    return;
                }

                const gp = result.data?.group_products as
                    | {
                          data?: Record<string, unknown>[];

                          total?: number;

                          per_page?: number;

                          current_page?: number;

                          last_page?: number;
                      }
                    | undefined;

                if (!gp) {
                    products.value = [];

                    pagination.value = {};

                    return;
                }

                products.value = Array.isArray(gp.data) ? (gp.data as any[]) : [];

                pagination.value = gp as Record<string, any>;
            } finally {
                loader.value = false;
            }
        };

        const handleCurrentChange = (num: number) => {
            clickHandler.value(num);
        };

        const openViewGroupProductsModal = (product_group: Record<string, unknown>) => {
            product_group_id.value = product_group.id as number;

            modal.value = new Modal(document.getElementById('modal_view_group_products'));

            modal.value.show();

            void getGroupProducts(1, true);
        };

        emitter.off('viewGroupProductsAction', openViewGroupProductsModal);

        emitter.on('viewGroupProductsAction', openViewGroupProductsModal);

        onUnmounted(() => {
            emitter.off('viewGroupProductsAction', openViewGroupProductsModal);
        });

        return {
            systemLocale,

            columns,

            loading,

            products,

            loader,

            pagination,

            handleCurrentChange,

            productDisplayName
        };
    }
});
